/**
 * App sidebar — shadcn-admin (satnaing) pattern.
 * Navigation: Dashboard / Leads / Pipeline / Clients / Billing / Reports.
 * No RBAC: both users see everything (TZ 1.3, 7.1).
 */

import { Link, useRouterState, useNavigate } from '@tanstack/react-router'
import {
  Bot,
  LayoutDashboard,
  Inbox,
  Kanban,
  Rocket,
  LifeBuoy,
  Users,
  CreditCard,
  BarChart3,
  LogOut,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'

interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
  /** Badge count for "Ждут взятия" / SLA alerts */
  badge?: number
}

const NAV_ITEMS: NavItem[] = [
  // Наша собственная Тоня — первым пунктом: к ней заходят чаще всего (07.08).
  { label: 'Наша Тоня', path: '/self-agent', icon: <Bot className="h-4 w-4" /> },
  { label: 'Дашборд', path: '/dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: 'Лиды', path: '/leads', icon: <Inbox className="h-4 w-4" /> },
  { label: 'Воронка', path: '/pipeline', icon: <Kanban className="h-4 w-4" /> },
  { label: 'Внедрение', path: '/delivery', icon: <Rocket className="h-4 w-4" /> },
  { label: 'Поддержка', path: '/support', icon: <LifeBuoy className="h-4 w-4" /> },
  { label: 'Клиенты', path: '/clients', icon: <Users className="h-4 w-4" /> },
  { label: 'Биллинг', path: '/billing', icon: <CreditCard className="h-4 w-4" /> },
  { label: 'Отчёты', path: '/reports', icon: <BarChart3 className="h-4 w-4" /> },
]

interface SidebarProps {
  unclaimedCount?: number
  className?: string
}

export function Sidebar({ unclaimedCount = 0, className }: SidebarProps) {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  async function handleLogout() {
    await logout()
    navigate({ to: '/login' })
  }

  return (
    <aside
      className={cn(
        'flex flex-col h-screen w-56 border-r bg-card text-card-foreground shrink-0',
        className
      )}
    >
      {/* Brand */}
      <div className="flex h-14 items-center px-4 border-b">
        <span className="font-semibold text-sm tracking-wide">TouchBrand · stars</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {NAV_ITEMS.map((item) => {
          const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/')
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {item.icon}
              <span>{item.label}</span>
              {/* Badge for leads inbox (TZ 4.0 "бейджи непрочитанного") */}
              {item.path === '/leads' && unclaimedCount > 0 && (
                <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
                  {unclaimedCount > 9 ? '9+' : unclaimedCount}
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Footer: user + logout */}
      <div className="border-t p-4 space-y-1">
        {user && (
          <div className="px-3 py-1 text-xs text-muted-foreground truncate">
            {user.display_name} · {user.email}
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <LogOut className="h-4 w-4" />
          <span>Выйти</span>
        </button>
      </div>
    </aside>
  )
}
